import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { toast } from "react-toastify";
import {
  GetParticipantSnapshotAsync,
  ParticipantSnapshotDto,
} from "../../../services/participantSnapshotService/ParticipantSnapshotService";
import {
  addCustomTagToParticipantAsync,
  addStandardTagToParticipantAsync,
  getParticipantTagsAsync,
  getParticipantTagOptionsAsync,
  getParticipantInfo,
  removeTagFromParticipantAsync,
  updateParticipantDescriptionAsync,
  ParticipantTagAssignmentResponseDto,
} from "../../../services/participantServices/ParticipantService";
import Loader from "../loader/Loader";
import { useParams, useNavigate } from "react-router-dom";
import "./ParticipantSnapshot.css";
import lefticon from "../../../assets/images/leftarrowicon.svg";
import {
  Calendar,
  ChevronDown,
  CircleAlert,
  CircleCheckBig,
  Clock4,
  FileText,
  Info,
  User,
} from "lucide-react";
import AvatarContainerbg from "../../../assets/images/AvatarContainerbg.svg";
import Recenttasktab from "../serviceoverviewtab/recenttasktab/Recenttasktab";
import RecentFileTab from "../serviceoverviewtab/RecentFilePageTab/RecentFileTab";
import Infotab from "../infotab/Infotab";
import Hometabs from "../hometabs/Hometabs";
import Pagination from "../../ui/Pagination/Pagination";
const ParticipantSnapshotTab: React.FC = () => {
  const { currentUser } = useAuth();
  const [snapshot, setSnapshot] = useState<ParticipantSnapshotDto | null>(null);
  let imgurl =
    "https://fastly.picsum.photos/id/27/3264/1836.jpg?hmac=p3BVIgKKQpHhfGRRCbsi2MCAzw8mWBCayBsKxxtWO8g";
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("files");
  const [authToken, setAuthToken] = useState<string>("");
  const [participant, setParticipant] = useState<any>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState<string>("");
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string>("");
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [tags, setTags] = useState<ParticipantTagAssignmentResponseDto[]>([]);
  const [standardTags, setStandardTags] = useState<{ id: number; name: string }[]>([]);
  const [allTags, setAllTags] = useState<{ id: number; name: string; custom: boolean }[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagActionLoadingById, setTagActionLoadingById] = useState<Record<number, boolean>>({});
  const [tagAddLoading, setTagAddLoading] = useState(false);
  const [tagError, setTagError] = useState<string>("");
  const [newTagInput, setNewTagInput] = useState("");
  const [tagInputValidationError, setTagInputValidationError] = useState<string>("");
  const [filesPagination, setFilesPagination] = useState<{ pageNumber: number; pageSize: number }>({ pageNumber: 1, pageSize: 10 });
  const [tasksPagination, setTasksPagination] = useState<{ pageNumber: number; pageSize: number }>({ pageNumber: 1, pageSize: 10 });
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [totalTasks, setTotalTasks] = useState<number>(0);

  const { id } = useParams();
  const participantId = Number(id);
  const navigate = useNavigate();

  useEffect(() => {
    fetchParticipantSnapshot();
    fetchParticipantInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showTagsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTagsModal]);

  useEffect(() => {
    if (participant) {
      setDescriptionDraft(participant?.description || "");
    }
  }, [participant]);

  useEffect(() => {
    if (currentUser && participant?.organizationId) {
      fetchAllTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, participant?.organizationId]);

  // Reset pagination when tab changes
  useEffect(() => {
    setFilesPagination({ pageNumber: 1, pageSize: 10 });
    setTasksPagination({ pageNumber: 1, pageSize: 10 });
  }, [activeTab]);

  const participantTags = useMemo(() => {
    const tags = participant?.tags ?? [];
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    return [];
  }, [participant]);

  const normalizedParticipantTags = useMemo(() => {
    return participantTags
      .map((t: any) => {
        if (!t) return null;
        if (typeof t === "string") {
          return {
            id: undefined as unknown as number,
            name: t,
            isCustom: true,
          };
        }

        const id =
          typeof t.id === "number"
            ? t.id
            : typeof t.tagId === "number"
              ? t.tagId
              : undefined;
        const name = String(t.name ?? t.tagName ?? "").trim();
        const isCustom =
          typeof t.isCustom === "boolean"
            ? t.isCustom
            : typeof t.custom === "boolean"
              ? t.custom
              : undefined;

        if (!name) return null;
        return { id, name, isCustom };
      })
      .filter(Boolean);
  }, [participantTags]);

  const filteredParticipantTags = useMemo(() => {
    return (normalizedParticipantTags as any[]).filter(
      (t: any) => t && t.name && t.name.length <= 13
    );
  }, [normalizedParticipantTags]);

  const participantTagNames = useMemo(() => {
    return new Set(
      (normalizedParticipantTags as any[]).map((t: any) =>
        String(t.name).toLowerCase()
      )
    );
  }, [normalizedParticipantTags]);

  const suggestedTags = useMemo(() => {
    const input = newTagInput.trim().toLowerCase();
    const filtered = allTags.filter((t) => t.name.length <= 13);
    if (!input) return filtered.slice(0, 8);
    return filtered
      .filter((t) => t.name.toLowerCase().includes(input))
      .slice(0, 8);
  }, [newTagInput, allTags]);

  const fetchParticipantSnapshot = async () => {
    if (!participantId || isNaN(participantId)) {
      toast.error("Invalid participant ID");
      return;
    }

    setLoading(true);
    try {
      const token = await currentUser?.getIdToken();
      if (!token) {
        toast.error("User not authenticated");
        setLoading(false);
        return;
      }
      if (token) {
        setAuthToken(token);
      }

      const res = await GetParticipantSnapshotAsync(token, participantId);
      if (res.statusCode === 200 || res.statusCode === 201) {
        setSnapshot(res.data);
      } else {
        setSnapshot(null);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to load participant snapshot");
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const fetchParticipantInfo = async () => {
    if (!participantId || !currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await getParticipantInfo(participantId.toString(), token);
      if (response !== null && response !== "unauthorized") {
        setParticipant(response);
        // Set description immediately when participant is loaded
        setDescriptionDraft((response as any)?.description || "");
        // Refresh tags after participant is loaded
        await refreshParticipantTags();
      }
    } catch (error) {
      console.error("Error fetching participant info:", error);
    }
  };

  const fetchAllTags = async () => {
    if (!currentUser) return;
    const orgId = participant?.organizationId;
    if (!orgId || typeof orgId !== "number") return;

    try {
      setTagsLoading(true);
      const token = await currentUser.getIdToken();
      const res = await getParticipantTagOptionsAsync(token, orgId);
      if (res.success && Array.isArray(res.data)) {
        const allTagData = res.data.map((t) => ({
          id: t.id,
          name: t.name,
          custom: t.custom,
        }));
        setAllTags(allTagData);
        setStandardTags(allTagData.map((t) => ({ id: t.id, name: t.name })));
      } else {
        setAllTags([]);
        setStandardTags([]);
      }
    } catch {
      setAllTags([]);
      setStandardTags([]);
    } finally {
      setTagsLoading(false);
    }
  };

  const refreshParticipantTags = async () => {
    if (!currentUser || !participantId) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await getParticipantTagsAsync(token, participantId.toString());
      if (res.success && Array.isArray(res.data)) {
        setTags(res.data);
        const tagData = res.data.map((t: any) => ({
          id: t.tagId,
          name: t.tagName,
          isCustom: t.custom,
        }));
        setParticipant((prev: any) => {
          if (prev) {
            return {
              ...prev,
              tags: tagData,
            };
          } else {
            // If participant is not loaded yet, create a minimal object with tags
            return {
              tags: tagData,
            };
          }
        });
      }
    } catch {
      // best-effort refresh; keep UI state as-is
    }
  };

  const handleSaveDescription = async () => {
    if (!currentUser || !participantId) return;

    try {
      setDescriptionSaving(true);
      setDescriptionError("");
      const token = await currentUser.getIdToken();
      const res = await updateParticipantDescriptionAsync(
        token,
        participantId.toString(),
        descriptionDraft
      );
      if (res.success) {
        setParticipant((prev: any) => ({
          ...prev,
          description: descriptionDraft,
        }));
        setIsEditingDescription(false);
        toast.success("Description updated successfully");
      } else {
        setDescriptionError(res.message || "Failed to update description.");
        toast.error(res.message || "Failed to update description.");
      }
    } catch {
      setDescriptionError("Failed to update description.");
      toast.error("Failed to update description.");
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleCancelDescription = () => {
    setDescriptionDraft(participant?.description || "");
    setIsEditingDescription(false);
    setDescriptionError("");
  };

  const assignTagFromSuggestion = async (tag: {
    id: number;
    name: string;
    custom: boolean;
  }) => {
    if (!currentUser || !participantId) return;

    const tagName = tag.name;
    if (participantTagNames.has(tagName.toLowerCase())) {
      setTagError("");
      return;
    }

    try {
      setTagAddLoading(true);
      setTagError("");
      const token = await currentUser.getIdToken();

      const res = tag.custom
        ? await addCustomTagToParticipantAsync(token, participantId.toString(), tagName)
        : await addStandardTagToParticipantAsync(token, participantId.toString(), tag.id);

      if (res.success) {
        await refreshParticipantTags();
        await fetchAllTags();
        setNewTagInput("");
        setShowTagsModal(false);
        toast.success("Tag added successfully");
      } else {
        setTagError(res.message || "Failed to add tag.");
        toast.error(res.message || "Failed to add tag.");
      }
    } catch {
      setTagError("Failed to add tag.");
      toast.error("Failed to add tag.");
    } finally {
      setTagAddLoading(false);
    }
  };

  const addTagByName = async (name: string) => {
    if (!currentUser || !participantId) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    if (participantTagNames.has(trimmed.toLowerCase())) {
      setTagInputValidationError("This tag is already added");
      setTagError("");
      return;
    }

    try {
      setTagAddLoading(true);
      setTagError("");
      const token = await currentUser.getIdToken();
      const standard = standardTags.find(
        (t) => t.name.toLowerCase() === trimmed.toLowerCase()
      );

      const res = standard
        ? await addStandardTagToParticipantAsync(
          token,
          participantId.toString(),
          standard.id
        )
        : await addCustomTagToParticipantAsync(token, participantId.toString(), trimmed);

      if (res.success) {
        await refreshParticipantTags();
        await fetchAllTags();
        setNewTagInput("");
        setTagInputValidationError("");
        setShowTagsModal(false);
        toast.success("Tag added successfully");
      } else {
        setTagError(res.message || "Failed to add tag.");
        toast.error(res.message || "Failed to add tag.");
      }
    } catch {
      setTagError("Failed to add tag.");
      toast.error("Failed to add tag.");
    } finally {
      setTagAddLoading(false);
    }
  };

  const removeTag = async (tag: any) => {
    if (!currentUser || !participantId) return;
    if (!tag?.id) {
      setTagError("Unable to remove this tag.");
      return;
    }

    try {
      setTagError("");
      setTagActionLoadingById((prev) => ({ ...prev, [tag.id]: true }));
      const token = await currentUser.getIdToken();
      const res = await removeTagFromParticipantAsync(
        token,
        participantId.toString(),
        tag.id
      );
      if (res.success) {
        await refreshParticipantTags();
        await fetchAllTags();
        toast.success("Tag removed successfully");
      } else {
        setTagError(res.message || "Failed to remove tag.");
        toast.error(res.message || "Failed to remove tag.");
      }
    } catch {
      setTagError("Failed to remove tag.");
      toast.error("Failed to remove tag.");
    } finally {
      setTagActionLoadingById((prev) => ({ ...prev, [tag.id]: false }));
    }
  };

  const validateTagName = (tagName: string): string | undefined => {
    const trimmed = tagName.trim();
    if (!trimmed || trimmed.length === 0) {
      return "Tag name cannot be empty";
    }
    if (trimmed.length < 2) {
      return "Tag name must be at least 2 characters (max 13 characters)";
    }
    if (trimmed.length > 13) {
      return "Tag name must not exceed 13 characters (min 2 characters)";
    }
    if (participantTagNames.has(trimmed.toLowerCase())) {
      return "This tag is already added";
    }
    return undefined;
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewTagInput(value);

    // Validate in real-time
    const error = validateTagName(value);
    setTagInputValidationError(error || "");

    // Clear tagError when user starts typing
    if (tagError) {
      setTagError("");
    }
  };

  const handleAddNewTag = () => {
    const trimmed = newTagInput.trim();
    if (!trimmed) {
      setTagInputValidationError("Tag name cannot be empty");
      return;
    }

    const error = validateTagName(trimmed);
    if (error) {
      setTagInputValidationError(error);
      return;
    }

    setTagInputValidationError("");
    addTagByName(trimmed);
  };

  const handleCancelTags = () => {
    setNewTagInput("");
    setTagError("");
    setTagInputValidationError("");
    setShowTagsModal(false);
  };

  const handleAddButtonClick = () => {
    const trimmed = newTagInput.trim();
    if (!trimmed) {
      setTagInputValidationError("Tag name cannot be empty");
      return;
    }

    const error = validateTagName(trimmed);
    if (error) {
      setTagInputValidationError(error);
      return;
    }

    handleAddNewTag();
  };

  const handleOpenTagsModal = () => {
    setNewTagInput("");
    setTagError("");
    setShowTagsModal(true);
  };

  // Static data for tabs
  const files = [
    {
      name: "Training_Manual.pdf",
      size: "2.4 MB",
      uploadedBy: "Sofia King",
      time: "2 days ago",
      signed: true,
    },
    {
      name: "Safety_Protocols.docx",
      size: "1.8 MB",
      uploadedBy: "Sarah Markle",
      time: "5 days ago",
      signed: false,
    },
    {
      name: "Attendance_Sheet.xlsx",
      size: "654 KB",
      uploadedBy: "Kristin Saunce",
      time: "1 week ago",
      signed: false,
    },
  ];

  const recentTasks = [
    {
      id: 1,
      code: "SRA-527",
      participant: "Emilia Clarke",
      title: "Emergency Response Training",
      assignee: "Sofia King",
      date: "Aug 24, 2025",
      frequency: "Monthly",
      status: "Issues found",
      lastActivity: "2 days ago",
    },
    {
      id: 2,
      code: "SRA-527",
      participant: "Emilia Clarke",
      title: "Emergency Response Training",
      assignee: "Sofia King",
      date: "Aug 24, 2025",
      frequency: "Monthly",
      status: "Issues found",
      lastActivity: "2 days ago",
    },
    {
      id: 3,
      code: "SRA-527",
      participant: "Emilia Clarke",
      title: "Emergency Response Training",
      assignee: "Sofia King",
      date: "Aug 24, 2025",
      frequency: "Monthly",
      status: "Issues found",
      lastActivity: "2 days ago",
    },
    {
      id: 4,
      code: "SRA-527",
      participant: "Emilia Clarke",
      title: "Emergency Response Training",
      assignee: "Sofia King",
      date: "Aug 24, 2025",
      frequency: "Monthly",
      status: "Issues found",
      lastActivity: "2 days ago",
    },
  ];

  const surveys = [
    {
      folder: "Sample Folder",
      name: "Emergency_survey_1",
      status: "Completed",
    },
    {
      folder: "Sample Folder",
      name: "Emergency_survey_1",
      status: "Not started",
    },
    {
      folder: "Sample Folder",
      name: "Emergency_survey_1",
      status: "Completed",
    },
    {
      folder: "Sample Folder",
      name: "Emergency_survey_1",
      status: "Not started",
    },
    {
      folder: "Sample Folder",
      name: "Emergency_survey_1",
      status: "Completed",
    },
  ];
  // Online status: 'online', 'offline', 'break'
  const onlineStatus = "online"; // Change based on your data

  const getFileIcon = (filename: string) => {
    if (filename.endsWith(".pdf")) {
      return (
        <div className="ps-file-icon-wrapper pdf">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
              fill="#E7000B"
            />
            <path
              d="M7 6H13M7 9H11"
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    } else if (filename.endsWith(".docx")) {
      return (
        <div className="ps-file-icon-wrapper docx">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
              fill="#155DFC"
            />
            <path
              d="M7 6H13M7 9H11"
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    } else if (
      filename.endsWith(".xlsx") ||
      filename.endsWith(".xls") ||
      filename.endsWith(".csv")
    ) {
      return (
        <div className="ps-file-icon-wrapper xlsx">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
              fill="#00A63E"
            />
            <path
              d="M7 6H13M7 9H11"
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="ps-file-icon-wrapper other">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M4 2C4 1.44772 4.44772 1 5 1H15C15.5523 1 16 1.44772 16 2V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
              fill="#E7000B"
            />
            <path
              d="M7 6H13M7 9H11"
              stroke="white"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      );
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {/* breadcrumb */}
      <div style={{
        display: "flex",
        width: "96.25rem",
        height: "2.8125rem",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "0.5rem",
        // padding: "0 1.5rem",
      }}>
        <span
          onClick={() => navigate("/drive/caseFileDrive")}
          style={{
            color: "#7E57C2",
            textAlign: "center",
            fontFamily: "Poppins",
            fontSize: "1rem",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "1.25rem",
            cursor: "pointer",
          }}
        >
          Drive
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="#99A1AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span
          onClick={() => navigate("/drive/caseFileDrive")}
          style={{
            color: "#7E57C2",
            fontFamily: "Poppins",
            fontSize: "1rem",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "1.25rem",
            cursor: "pointer",
          }}
        >
          Participant Files
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="#99A1AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{
          color: "#364153",
          fontFamily: "Poppins",
          fontSize: "1rem",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "1.25rem",
        }}>
          {snapshot?.name || (participant?.firstName && participant?.lastName ? `${participant.firstName} ${participant.lastName}` : "-")}
        </span>
      </div>

      <div className="participant-snapshot">
        {/* Header */}
        <div className="ps-header">
          <div className="flex items-center gap-4">
            <button className="ps-back-btn" onClick={() => navigate(-1)}>
              <img
                src={lefticon}
                alt="backicon"
                className="w-[1rem] h-[1rem]"
              />
            </button>
            <h1 className="ps-header-title">
              {snapshot?.name || "-"}
            </h1>
          </div>
          {/* <div className="ps-header-actions flex-wrap"> */}
          <div className="ps-header-actions">
            {participant?.isCompliant ? (
              <button className="ps-status-btn complete">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <g clipPath="url(#clip0_2695_6094)">
                    <path d="M14.1078 6.47061C14.4033 7.92086 14.1927 9.42859 13.5111 10.7424C12.8295 12.0561 11.7181 13.0965 10.3623 13.69C9.00645 14.2836 7.48812 14.3944 6.06049 14.0039C4.63287 13.6135 3.38225 12.7454 2.51719 11.5445C1.65212 10.3435 1.22491 8.88235 1.30679 7.40456C1.38867 5.92678 1.97469 4.52172 2.96713 3.42372C3.95957 2.32571 5.29844 1.60112 6.76046 1.37078C8.22248 1.14044 9.71928 1.41828 11.0012 2.15796" stroke="#016630" strokeWidth="1.29412" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.82227 7.11761L7.76344 9.05878L14.234 2.5882" stroke="#016630" strokeWidth="1.29412" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2695_6094">
                      <rect width="15.5294" height="15.5294" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                Compliant
              </button>
            ) : (
              <button className="ps-status-btn non-compliant">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  {/* Changed fill="#747474" to fill="currentColor" */}
                  <path d="M7.5 15C3.35294 15 0 11.6471 0 7.5C0 3.35294 3.35294 0 7.5 0C11.6471 0 15 3.35294 15 7.5C15 11.6471 11.6471 15 7.5 15ZM7.5 0.882353C3.83824 0.882353 0.882353 3.83824 0.882353 7.5C0.882353 11.1618 3.83824 14.1176 7.5 14.1176C11.1618 14.1176 14.1176 11.1618 14.1176 7.5C14.1176 3.83824 11.1618 0.882353 7.5 0.882353Z" fill="currentColor" />
                  <path d="M7.05859 10.5882H7.94095V11.4706H7.05859V10.5882ZM7.76448 9.70589H7.23506L7.05859 6.17648V3.52942H7.94095V6.17648L7.76448 9.70589Z" fill="currentColor" />
                </svg>
                Non Compliant
            </button>
            )}
          </div>
          {/* <div className="ps-last-activity">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle
                  cx="8"
                  cy="8"
                  r="6.5"
                  stroke="#6B7280"
                  strokeWidth="1"
                />
                <path
                  d="M8 4V8L10.5 10.5"
                  stroke="#6B7280"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
              <div className="ps-activity-text ps-text">
                <div>
                  Last Activity: {lastContact.type} • {lastContact.date}
                </div>
                <div className="ps-support-coordinator ps-text">
                  Support Coordinator:{" "}
                  <span className="ps-text-content">
                    {snapshot?.primarySC?.displayName || "Bessie Cooper"}
                  </span>
                </div>
              </div>
            </div> */}
          {/* </div> */}
        </div>

        <div className="ps-divider"></div>

        <div className="profile-main w-full">
          {/* Profile Section - Moved to ParticipantSnapshotLeftSection.tsx */}
          {/* Backup: This section has been moved to ParticipantSnapshotLeftSection.tsx component */}
          {/* 
          <div className="ps-profile-section  items-start w-full md:w-[40%]  border border-[#C9C9C9] rounded-[10px]">
            ... (backup code removed, see ParticipantSnapshotLeftSection.tsx)
          </div>
          */}

          {/* <div className="ps-divider"></div> */}

          <div className="w-full ">
            {/* Tabs */}
            <div className="ps-tabs w-full">
              <div className="tab-content-profile px-1  w-[80%]">
                <button
                  className={` ps-tab ${activeTab === "files" ? "active" : ""}`}
                  onClick={() => setActiveTab("files")}
                >
                  Files
                </button>
                <button
                  className={`ps-tab ${activeTab === "recent-task" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("recent-task")}
                >
                  Recent Tasks
                </button>
                <button
                  className={`ps-tab ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => setActiveTab("info")}
                >
                  Info
                </button>
                <button
                  className={`ps-tab ${activeTab === "survey-history" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("survey-history")}
                >
                  Survey History
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="ps-tab-content">
              {activeTab === "files" && (
                <RecentFileTab
                  authToken={authToken}
                  participantId={participantId + ""}
                  pagination={filesPagination}
                  setPagination={setFilesPagination}
                  setTotalItems={setTotalFiles}
                />
              )}

              {activeTab === "recent-task" && (
                <Recenttasktab
                  pagination={tasksPagination}
                  setPagination={setTasksPagination}
                  setTotalItems={setTotalTasks}
                />
              )}

              {activeTab === "info" && <Infotab />}

              {activeTab === "survey-history" && (
                <div className="ps-tab-content relative min-h-[17.5rem]">
                  <div className="coming-soon-wrapper">
                    <div className="coming-soon-content">
                      <div className="icon-wrapper">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="19"
                          height="22"
                          viewBox="0 0 19 22"
                          fill="none"
                        >
                          <path
                            d="M15.26 6.24L16.75 4.75M15.26 6.24C13.6659 4.64594 11.5043 3.75 9.25 3.75C6.99566 3.75 4.83365 4.64553 3.23959 6.23959C1.64553 7.83365 0.75 9.99566 0.75 12.25C0.75 14.5043 1.64553 16.6663 3.23959 18.2604C4.83365 19.8545 6.99566 20.75 9.25 20.75C11.5043 20.75 13.6663 19.8545 15.2604 18.2604C16.8545 16.6663 17.75 14.5043 17.75 12.25C17.75 9.99566 16.8541 7.83406 15.26 6.24Z"
                            stroke="#747474"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9.25 17.75C7.79131 17.75 6.39236 17.1705 5.36091 16.1391C4.32946 15.1076 3.75 13.7087 3.75 12.25C3.75 10.7913 4.32946 9.39236 5.36091 8.36091C6.39236 7.32946 7.79131 6.75 9.25 6.75M11.75 0.75H6.75M9.25 12.25L12.75 8.75"
                            stroke="#747474"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="coming-soon-text">Coming Soon</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Common Pagination - Same position for both files and recent-task tabs */}

            </div>

          </div>
          {activeTab === "files" && totalFiles > 0 && (
            <div className="ps-pagination-container">
              <Pagination
                currentPage={filesPagination.pageNumber}
                totalPages={Math.ceil(totalFiles / filesPagination.pageSize)}
                onPageChange={(p) => setFilesPagination(prev => ({ ...prev, pageNumber: p }))}
                pageSize={filesPagination.pageSize}
                resetPageSize={(pageNumber, pageSize) => setFilesPagination({ pageNumber, pageSize })}
              />
            </div>
          )}

          {activeTab === "recent-task" && totalTasks > 0 && (
            <div className="ps-pagination-container">
              <Pagination
                currentPage={tasksPagination.pageNumber}
                totalPages={Math.ceil(totalTasks / tasksPagination.pageSize)}
                onPageChange={(p) => setTasksPagination(prev => ({ ...prev, pageNumber: p }))}
                pageSize={tasksPagination.pageSize}
                resetPageSize={(pageNumber, pageSize) => setTasksPagination({ pageNumber, pageSize })}
              />
            </div>
          )}
        </div>
      </div>

      {/* Participant Tags Modal */}
      {showTagsModal && (
        <div className="ps-tags-modal-overlay" onClick={handleCancelTags}>
          <div className="ps-tags-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ps-tags-modal-header">
              <h2 className="ps-tags-modal-title">Participant Tags</h2>
              <button className="ps-tags-modal-close" onClick={handleCancelTags} type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 4L12 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="ps-tags-modal-content">
              {/* Added tags section */}
              <div className="ps-tags-section">
                <h3 className="ps-tags-section-title">Added tags</h3>
                <div className="ps-tags-container">
                  {filteredParticipantTags.length === 0 ? (
                    <div className="text-gray-500 text-sm py-2">
                      No tags added yet
                    </div>
                  ) : (
                    filteredParticipantTags.map((t: any, idx: number) => {
                      const key = t?.id ? String(t.id) : `${t.name}-${idx}`;
                      const isRemoving = t?.id ? !!tagActionLoadingById[t.id] : false;
                      return (
                        <div key={key} className="ps-tag-added">
                          <span>{t.name}</span>
                          <button
                            className="ps-tag-remove-btn"
                            onClick={() => removeTag(t)}
                            disabled={isRemoving || !t?.id}
                            type="button"
                            title={!t?.id ? "Unable to remove this tag" : "Remove tag"}
                          >
                            {isRemoving ? (
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M12 4L4 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4 4L12 12" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Type to add tag section */}
              <div className="ps-tags-section">
                <h3 className="ps-tags-section-title">Type to add tag</h3>
                <div className="ps-tags-input-wrapper">
                  <input
                    type="text"
                    className={`ps-tags-input ${tagInputValidationError ? "error" : ""}`}
                    value={newTagInput}
                    onChange={handleTagInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNewTag();
                      }
                    }}
                    placeholder={tagsLoading ? "Loading tags..." : "Type tag name..."}
                    disabled={tagsLoading || tagAddLoading}
                    maxLength={13}
                  />
                </div>
                {tagInputValidationError && (
                  <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                    {tagInputValidationError}
                  </span>
                )}
                {tagError && !tagInputValidationError && (
                  <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                    {tagError}
                  </span>
                )}
                {!tagInputValidationError && !tagError && (
                  <span style={{ color: "#6c757d", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                    Tag name must be between 2 and 13 characters
                  </span>
                )}
              </div>

              {/* Available tags section */}
              {/* <div className="ps-tags-section">
                <h3 className="ps-tags-section-title">Available tags</h3>
                {suggestedTags.length > 0 ? (
                  <div className="ps-tags-container">
                    {suggestedTags.map((tag) => {
                      const isAlreadyAdded = participantTagNames.has(tag.name.toLowerCase());
                      return (
                        <button
                          key={tag.id}
                          className="ps-tag-available"
                          onClick={() => assignTagFromSuggestion(tag)}
                          disabled={tagAddLoading || isAlreadyAdded}
                          type="button"
                        >
                          {tag.name}
                          {tag.custom && <span className="ml-1 text-xs">*</span>}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm py-2">
                    {tagsLoading ? "Loading tags..." : "No available tags"}
                  </div>
                )}
              </div> */}
            </div>

            <div className="ps-tags-modal-footer">
              <button className="ps-tags-modal-btn ps-tags-modal-btn-cancel" onClick={handleCancelTags} type="button">
                Cancel
              </button>
              <button className="ps-tags-modal-btn ps-tags-modal-btn-add" onClick={handleAddButtonClick} type="button" disabled={tagAddLoading}>
                {tagAddLoading ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantSnapshotTab;
